import { Controller } from "@hotwired/stimulus"
import { getCsrfToken } from "shared/form_helpers"
import { sendSuccessNotification, sendFailureNotification } from "shared/toast_notifications"

// Connects to data-controller="settings--task-statuses-table"
export default class extends Controller {
    static targets = [
        "table",
        "createModal",
        "createForm",
        "nameInput",
        "testingTaskIdInput",
        "editModal",
        "editForm",
        "editNameInput",
        "editIdInput",
        "editTestingTaskIdInput"
    ]

    static values = {
        taskId: String
    }

    connect() {
        this.initializeTable()
        this.loadTaskStatuses()
    }

    initializeTable() {
        // No initialization needed — argus--table controller handles this
    }

    _getArgusTable() {
        if (!this.hasTableTarget) return null
        return this.application.getControllerForElementAndIdentifier(
            this.tableTarget, 'argus--table'
        )
    }

    async loadTaskStatuses() {
        if (!this.taskIdValue) {
            console.error('No task ID provided')
            return
        }

        const argusTable = this._getArgusTable()
        argusTable?.showLoading()
        try {
            const response = await fetch(`/api/testing_tasks/describe?id=${this.taskIdValue}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch task statuses')
            }

            const data = await response.json()
            
            // The API returns the data wrapped in a 'data' object
            const taskData = data.data || data
            const statuses = taskData.states || []
            
            this.populateTable(statuses)
        } catch (error) {
            console.error('Error loading task statuses:', error)
            sendFailureNotification('Failed to load task statuses')
        } finally {
            argusTable?.hideLoading()
        }
    }

    populateTable(statuses) {
        if (this.hasTableTarget) {
            const argusTable = this.application.getControllerForElementAndIdentifier(
                this.tableTarget, 'argus--table'
            )
            if (argusTable) {
                argusTable.loadData(statuses)
            }
        }
    }

    openCreateModal(event) {
        event.preventDefault()
        
        // Clear form
        if (this.hasNameInputTarget) {
            this.nameInputTarget.value = ''
        }
        
        // Set task ID
        if (this.hasTestingTaskIdInputTarget) {
            this.testingTaskIdInputTarget.value = this.taskIdValue
        }

        // Show modal
        const modal = bootstrap.Modal.getOrCreateInstance(this.createModalTarget)
        modal.show()
    }

    async handleCreateSubmit(event) {
        event.preventDefault()
        event.stopPropagation()

        const formData = new FormData(this.createFormTarget)

        try {
            const response = await fetch('/api/testing_tasks/create_status', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.error || 'Failed to create task status')
            }

            // Close modal
            const modal = bootstrap.Modal.getInstance(this.createModalTarget)
            modal.hide()

            // Reload table
            await this.loadTaskStatuses()

            sendSuccessNotification('Task status created successfully')
        } catch (error) {
            console.error('Error creating task status:', error)
            sendFailureNotification(error.message || 'Failed to create task status')
        }
    }

    openEditModal(event) {
        event.preventDefault()

        const button = event.currentTarget
        const statusId = button.dataset.statusId
        const statusName = button.dataset.statusName

        // Populate edit form
        if (this.hasEditNameInputTarget) {
            this.editNameInputTarget.value = statusName || ''
        }
        if (this.hasEditIdInputTarget) {
            this.editIdInputTarget.value = statusId
        }
        if (this.hasEditTestingTaskIdInputTarget) {
            this.editTestingTaskIdInputTarget.value = this.taskIdValue
        }

        // Show modal
        const modal = bootstrap.Modal.getOrCreateInstance(this.editModalTarget)
        modal.show()
    }

    async handleEditSubmit(event) {
        event.preventDefault()
        event.stopPropagation()

        const formData = new FormData(this.editFormTarget)

        try {
            const response = await fetch('/api/testing_tasks/update_status', {
                method: 'PATCH',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.error || 'Failed to update task status')
            }

            // Close modal
            const modal = bootstrap.Modal.getInstance(this.editModalTarget)
            modal.hide()

            // Reload table
            await this.loadTaskStatuses()

            sendSuccessNotification('Task status updated successfully')
        } catch (error) {
            console.error('Error updating task status:', error)
            sendFailureNotification(error.message || 'Failed to update task status')
        }
    }

    async deleteTaskStatus(event) {
        event.preventDefault()

        const button = event.currentTarget
        const statusId = button.dataset.statusId
        const statusName = button.dataset.statusName || 'this status'

        if (!confirm(`Are you sure you want to delete ${statusName}?`)) {
            return
        }

        const formData = new FormData()
        formData.append('id', this.taskIdValue)
        formData.append('status_id', statusId)

        try {
            const response = await fetch('/api/testing_tasks/delete_status', {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.error || 'Failed to delete task status')
            }

            // Reload table
            await this.loadTaskStatuses()

            sendSuccessNotification('Task status deleted successfully')
        } catch (error) {
            console.error('Error deleting task status:', error)
            sendFailureNotification(error.message || 'Failed to delete task status')
        }
    }
}
